import { useState } from "react";
import PageHeader from "@/components/base/PageHeader";
import Card from "@/components/base/Card";
import Button from "@/components/base/Button";
import Badge from "@/components/base/Badge";
import StatCard from "@/components/base/StatCard";
import Tabs from "@/components/base/Tabs";
import { LoadingState } from "@/components/base/StateViews";
import { useToast } from "@/components/base/Toast";
import { useAsync } from "@/hooks/useAsync";
import { usePager } from "@/hooks/usePager";
import Pagination from "@/components/base/Pagination";
import {
  deleteTransaction,
  getCategories,
  getTransactions,
  computeTotals,
  resolveTxRef,
} from "@/services/financeService";
import { getChargeCategories, getCharges } from "@/services/chargeService";
import { getSchoolInfo, getCycles } from "@/services/schoolService";
import { useAuth } from "@/contexts/AuthContext";
import CycleScopeBanner from "@/components/feature/CycleScopeBanner";
import PeriodFilter from "@/components/feature/PeriodFilter";
import { ALL_PERIOD, inPeriod, periodLabel, type Period } from "@/utils/period";
import { formatMoney, formatDate, plural } from "@/utils/format";
import { annualOf, chargeHasAnyCycle, chargeTotalMonthly } from "@/utils/charges";
import type { Transaction } from "@/types";
import TransactionModal from "../finance/components/TransactionModal";
import PayrollTab from "./components/PayrollTab";
import DisbursementPrintModal from "./components/DisbursementPrintModal";
import ChargesPanel from "./components/ChargesPanel";
import ChargeCategoriesPanel from "./components/ChargeCategoriesPanel";
import { cycleIdOf } from "@/utils/cycleScope";

async function load() {
  const [transactions, categories, cycles, schoolInfo, charges, chargeCategories] = await Promise.all([
    getTransactions(),
    getCategories(),
    getCycles(),
    getSchoolInfo(),
    getCharges(),
    getChargeCategories(),
  ]);
  return { transactions, categories, cycles, schoolInfo, charges, chargeCategories };
}

export default function ExpensesPage() {
  const { notify } = useToast();
  const { allowedCycleIds, can } = useAuth();
  const { data, loading, reload } = useAsync(load, []);
  const [tab, setTab] = useState("depenses");
  const [cycleFilter, setCycleFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [period, setPeriod] = useState<Period>(ALL_PERIOD);
  const pager = usePager();
  const [modalOpen, setModalOpen] = useState(false);
  const [printOpen, setPrintOpen] = useState(false);
  const [editing, setEditing] = useState<Transaction | null>(null);

  if (loading || !data) return <LoadingState label="Chargement des dépenses..." />;
  const { transactions, categories, cycles, schoolInfo, charges, chargeCategories } = data;
  const primaireId = cycleIdOf(cycles, "PRIMAIRE");

  const visibleCycles = cycles.filter((c) => allowedCycleIds.includes(c.id));
  const scopeCycleIds = visibleCycles.map((c) => c.id);
  const scopedTransactions = transactions.filter((t) => allowedCycleIds.includes(t.cycleId));

  const refs = { categories, chargeCategories, charges };
  const cycleLabel = (id: string) => cycles.find((c) => c.id === id)?.label ?? "—";

  const scopedCharges = charges.filter((c) => chargeHasAnyCycle(c, scopeCycleIds));
  const budgetMonthly = scopedCharges.reduce((s, c) => s + chargeTotalMonthly(c, scopeCycleIds), 0);
  const budgetAnnual = annualOf(budgetMonthly);

  const expenses = scopedTransactions.filter((t) => t.type === "depense" && inPeriod(t.date, period));
  const totals = computeTotals(expenses);
  const filtered = expenses
    .filter((t) => cycleFilter === "all" || t.cycleId === cycleFilter)
    .filter((t) => {
      const q = search.trim().toLowerCase();
      if (!q) return true;
      return t.description.toLowerCase().includes(q) || resolveTxRef(t, refs).label.toLowerCase().includes(q);
    })
    .sort((a, b) => b.date.localeCompare(a.date));
  const page = pager.slice(filtered, `${cycleFilter}|${search}|${JSON.stringify(period)}`);

  const openForm = (t: Transaction | null) => {
    setEditing(t);
    setModalOpen(true);
  };

  const handleDelete = async (t: Transaction) => {
    await deleteTransaction(t.id);
    await reload();
    notify("Dépense supprimée.", "info");
  };

  return (
    <div className="animate-fade-in">
      <PageHeader
        title="Dépenses & sorties"
        subtitle="Charges prévisionnelles par catégorie, budget mensuel/annuel et suivi prévu vs réalisé"
        icon="ri-arrow-down-circle-line"
        actions={
          tab === "depenses" ? (
            <>
              <Button variant="outline" icon="ri-printer-line" onClick={() => setPrintOpen(true)}>
                Tableau des décaissements
              </Button>
              {can("expenses:create") && (
              <Button icon="ri-add-line" onClick={() => openForm(null)}>
                Enregistrer une dépense
              </Button>
              )}
            </>
          ) : tab === "charges" ? (
            <Badge tone="secondary" icon="ri-information-line">
              {plural(scopedCharges.length, "charge")} • budget annuel {formatMoney(budgetAnnual)}
            </Badge>
          ) : undefined
        }
      />

      <CycleScopeBanner className="mb-5" />

      <div className="mb-5 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <Tabs
          tabs={[
            { id: "depenses", label: "Dépenses", icon: "ri-arrow-down-line", count: expenses.length },
            { id: "charges", label: "Charges & catégories", icon: "ri-stack-line", count: scopedCharges.length },
            { id: "salaires", label: "Paie", icon: "ri-wallet-3-line" },
          ]}
          active={tab}
          onChange={setTab}
        />
        {tab === "depenses" && (
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3 py-1.5">
              <i className="ri-search-line text-foreground-400 text-sm"></i>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher une dépense..."
                className="w-44 bg-transparent text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none"
              />
            </div>
            <PeriodFilter value={period} onChange={setPeriod} />
            <select
              value={cycleFilter}
              onChange={(e) => setCycleFilter(e.target.value)}
              className="rounded-md border border-background-300 bg-background-50 px-3 py-1.5 text-sm cursor-pointer focus:outline-none"
            >
              <option value="all">Tous les cycles</option>
              {visibleCycles.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.label}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {tab === "depenses" && (
        <div className="space-y-5">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard label="Total des dépenses" value={formatMoney(totals.depenses)} icon="ri-arrow-down-circle-line" tone="accent" hint={periodLabel(period)} />
            <StatCard label="Budget annuel prévu" value={formatMoney(budgetAnnual)} icon="ri-stack-line" tone="primary" hint={`${formatMoney(budgetMonthly)} / mois`} />
            <StatCard label="Opérations" value={String(expenses.length)} icon="ri-file-list-3-line" tone="secondary" />
            <StatCard label="Charges actives" value={String(scopedCharges.length)} icon="ri-price-tag-3-line" tone="primary" />
          </div>

          <div>
            <Card padded={false}>
              <div className="border-b border-background-200 px-4 py-3">
                <h3 className="text-sm font-semibold font-heading text-foreground-950">
                  Sorties enregistrées ({filtered.length})
                </h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[680px] text-sm">
                  <thead>
                    <tr className="border-b border-background-200 text-left text-xs uppercase tracking-wide text-foreground-500">
                      <th className="px-4 py-3 font-medium">Date</th>
                      <th className="px-4 py-3 font-medium">Charge</th>
                      <th className="px-4 py-3 font-medium">Cycle</th>
                      <th className="px-4 py-3 font-medium">Description</th>
                      <th className="px-4 py-3 font-medium text-right">Montant</th>
                      <th className="px-4 py-3 font-medium text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {page.items.map((t) => {
                      const ref = resolveTxRef(t, refs);
                      return (
                        <tr key={t.id} className="border-b border-background-200 last:border-0 hover:bg-background-100/60 transition-colors">
                          <td className="px-4 py-3 text-xs text-foreground-500 whitespace-nowrap">{formatDate(t.date)}</td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <span className="w-7 h-7 rounded-md bg-accent-50 text-accent-700 flex items-center justify-center shrink-0">
                                <i className={`${ref.icon} text-sm`}></i>
                              </span>
                              <div className="min-w-0">
                                <span className="block text-foreground-800">{ref.label}</span>
                                {ref.familyName && <span className="block text-xs text-foreground-400">{ref.familyName}</span>}
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <Badge tone={t.cycleId === primaireId ? "warning" : "secondary"}>{cycleLabel(t.cycleId)}</Badge>
                          </td>
                          <td className="px-4 py-3 text-xs text-foreground-600 max-w-[200px] truncate">{t.description}</td>
                          <td className="px-4 py-3 text-right font-medium text-accent-700 whitespace-nowrap">{formatMoney(t.amount)}</td>
                          <td className="px-4 py-3">
                            {t.source ? (
                              <span className="flex justify-end text-xs text-foreground-400" title="Opération automatique : annulez le reçu ou la paie pour la retirer">
                                <i className="ri-lock-line mr-1"></i>{t.source === "receipt" ? "Reçu" : "Paie"}
                              </span>
                            ) : (
                            <div className="flex items-center justify-end gap-1">
                              <button
                                onClick={() => openForm(t)}
                                className="press w-10 h-10 sm:w-8 sm:h-8 flex items-center justify-center rounded-md text-foreground-500 hover:bg-background-200 transition-colors cursor-pointer"
                                aria-label="Modifier"
                                style={can("expenses:update") ? undefined : { display: "none" }}
                              >
                                <i className="ri-edit-line"></i>
                              </button>
                              <button
                                onClick={() => handleDelete(t)}
                                className="press w-10 h-10 sm:w-8 sm:h-8 flex items-center justify-center rounded-md text-foreground-400 hover:bg-accent-50 hover:text-accent-600 transition-colors cursor-pointer"
                                aria-label="Supprimer"
                                style={can("expenses:delete") ? undefined : { display: "none" }}
                              >
                                <i className="ri-delete-bin-6-line"></i>
                              </button>
                            </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                    {filtered.length === 0 && (
                      <tr>
                        <td colSpan={6} className="px-4 py-10 text-center text-sm text-foreground-400">
                          Aucune dépense pour ces filtres ou cette période.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <Pagination pager={page} className="border-t border-background-200" />
            </Card>
          </div>
        </div>
      )}

      {tab === "charges" && (
        <div className="space-y-5">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <StatCard label="Charges prévisionnelles" value={String(scopedCharges.length)} icon="ri-stack-line" tone="primary" />
            <StatCard label="Budget mensuel" value={formatMoney(budgetMonthly)} icon="ri-calendar-line" tone="secondary" />
            <StatCard label="Budget annuel" value={formatMoney(budgetAnnual)} icon="ri-calendar-2-line" tone="accent" />
          </div>
          <ChargesPanel charges={charges} categories={chargeCategories} cycles={visibleCycles} onChanged={reload} />
          <ChargeCategoriesPanel categories={chargeCategories} charges={charges} onChanged={reload} />
        </div>
      )}

      {tab === "salaires" && (
        <PayrollTab cycles={cycles} onChanged={reload} />
      )}

      <TransactionModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSaved={reload}
        type="depense"
        categories={categories}
        charges={charges}
        chargeCategories={chargeCategories}
        cycles={visibleCycles}
        editing={editing}
      />

      <DisbursementPrintModal
        open={printOpen}
        onClose={() => setPrintOpen(false)}
        transactions={scopedTransactions}
        charges={charges}
        chargeCategories={chargeCategories}
        cycles={visibleCycles}
        schoolName={schoolInfo.name}
        schoolYear={schoolInfo.schoolYear}
      />
    </div>
  );
}